/**
 * TechCheckPage - Equipment verification before video call
 * Nordic Sanctuary design: Calm, reassuring, step-by-step guidance
 */

import { useState } from "react";
import { useParams, useNavigate, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { ClientLayout } from "../components/layout/ClientLayout";
import { CheckProgress } from "../components/techcheck/CheckProgress";
import { CameraCheck } from "../components/techcheck/CameraCheck";
import { MicrophoneCheck } from "../components/techcheck/MicrophoneCheck";
import { SpeakerCheck } from "../components/techcheck/SpeakerCheck";
import { NetworkCheck } from "../components/techcheck/NetworkCheck";
import { CheckSummary } from "../components/techcheck/CheckSummary";
import { useSession } from "../hooks/useSession";

type CheckStatus = "pending" | "running" | "pass" | "warning" | "fail";

interface CheckState {
  camera: CheckStatus;
  microphone: CheckStatus;
  speaker: CheckStatus;
  network: CheckStatus;
}

export function TechCheckPage() {
  const { appointmentId } = useParams({ from: "/tech-check/$appointmentId" });
  const navigate = useNavigate();
  const { session, isValid, isLoading: sessionLoading } = useSession(appointmentId);
  const { t } = useTranslation("techcheck");
  const apiRef = api as any;

  const [currentStep, setCurrentStep] = useState(0);
  const [checkState, setCheckState] = useState<CheckState>({
    camera: "pending",
    microphone: "pending",
    speaker: "pending",
    network: "pending",
  });

  const prepWindow = useQuery(
    apiRef.video.admission.getPrepWindowStatus,
    appointmentId && session?.sessionToken
      ? { appointmentId: appointmentId as any, sessionToken: session.sessionToken }
      : "skip"
  ) as { allowed: boolean; opensAt: number | null; msUntilOpen: number | null } | undefined;

  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);

  // Check session - redirect to home if invalid (skip in dev mode for testing)
  if (!sessionLoading && !isValid && appointmentId && !import.meta.env.DEV) {
    return <Navigate to="/" search={{ id: appointmentId }} />;
  }
  const debugMode = testingMode?.enabled ?? false;
  if (!debugMode && prepWindow && !prepWindow.allowed) {
    const totalSeconds = Math.max(0, Math.ceil((prepWindow.msUntilOpen || 0) / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? "Tag" : "Tage"}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "Stunde" : "Stunden"}`);
    parts.push(`${mins} ${mins === 1 ? "Minute" : "Minuten"}`);
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-lg p-6 text-center space-y-4">
          <h1 className="text-2xl font-bold text-[var(--stone-800)]">Technik-Check noch gesperrt</h1>
          <p className="text-[var(--stone-600)]">
            Der Technik-Check ist 30 Minuten vor dem Termin verfügbar.
          </p>
          <p className="text-lg font-semibold text-[var(--sage-700)]">
            Freigabe in {parts.join(", ")}
          </p>
          <button
            onClick={() => navigate({ to: "/appointment/$appointmentId", params: { appointmentId } })}
            className="w-full px-4 py-3 rounded-xl bg-[var(--stone-100)] text-[var(--stone-700)] font-medium hover:bg-[var(--stone-200)] transition-colors"
          >
            Zurück zum Termin
          </button>
        </div>
      </ClientLayout>
    );
  }

  const steps = [
    { id: "camera", label: t("checks.camera.title"), status: checkState.camera },
    { id: "microphone", label: t("checks.microphone.title"), status: checkState.microphone },
    { id: "speaker", label: t("checks.speaker.title"), status: checkState.speaker },
    { id: "network", label: t("checks.network.title"), status: checkState.network },
  ];

  const updateCheck = (check: keyof CheckState, passed: boolean, isRequired: boolean) => {
    setCheckState((prev) => ({
      ...prev,
      [check]: passed ? "pass" : isRequired ? "fail" : "warning",
    }));
  };

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleComplete = () => {
    navigate({ to: "/video/$appointmentId", params: { appointmentId } });
  };

  const handleRetry = () => {
    setCurrentStep(0);
    setCheckState({
      camera: "pending",
      microphone: "pending",
      speaker: "pending",
      network: "pending",
    });
  };

  const summaryResults: Array<{
    id: "camera" | "microphone" | "speaker" | "network";
    name: string;
    status: CheckStatus;
    required: boolean;
  }> = [
    { id: "camera", name: t("checks.camera.title"), status: checkState.camera, required: true },
    { id: "microphone", name: t("checks.microphone.title"), status: checkState.microphone, required: true },
    { id: "speaker", name: t("checks.speaker.title"), status: checkState.speaker, required: false },
    { id: "network", name: t("checks.network.title"), status: checkState.network, required: false },
  ];

  return (
    <ClientLayout>
      <div className="client-card w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="text-center mb-2">
            <p className="text-xs font-medium uppercase tracking-wider text-[var(--sage-600)] mb-1">
              {t("stepOf", { current: Math.min(currentStep + 1, 4), total: 4 })}
            </p>
            <h1 className="text-2xl font-bold text-[var(--stone-800)]">
              {t("title")}
            </h1>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="px-6 py-5">
          <CheckProgress steps={steps} currentStep={currentStep} />
        </div>

        {/* Content */}
        <div className="p-6 pt-2 border-t border-[var(--stone-100)]">
          {currentStep === 0 && (
            <CameraCheck
              onResult={(passed) => updateCheck("camera", passed, true)}
              onContinue={nextStep}
            />
          )}

          {currentStep === 1 && (
            <MicrophoneCheck
              onResult={(passed) => updateCheck("microphone", passed, true)}
              onContinue={nextStep}
            />
          )}

          {currentStep === 2 && (
            <SpeakerCheck
              onResult={(passed) => updateCheck("speaker", passed, false)}
              onContinue={nextStep}
            />
          )}

          {currentStep === 3 && (
            <NetworkCheck
              onResult={(passed) => updateCheck("network", passed, false)}
              onContinue={nextStep}
            />
          )}

          {currentStep === 4 && (
            <CheckSummary
              results={summaryResults}
              onContinue={handleComplete}
              onRetry={handleRetry}
            />
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
